import type { ReportTypeEnum } from '@revelio/data-access';

import { GLOBAL_ASSISTANT_REPORT_CATALOG } from './catalog-data';
import type {
  GlobalAssistantReportCatalogEntry,
  GlobalAssistantReportOfferCatalogEntry,
  GlobalAssistantReportParameterCatalogEntry,
  GlobalAssistantReportParameterDescriptor,
  GlobalAssistantReportParameterMapping,
} from './types';

export { GLOBAL_AI_ASSISTANT_REPORT_SELECTOR_CONFIDENCE_THRESHOLD } from './catalog-data';
export type {
  GlobalAssistantReportCatalogEntry,
  GlobalAssistantReportOfferCatalogEntry,
  GlobalAssistantReportParameterCatalogEntry,
  GlobalAssistantReportParameterDescriptor,
  GlobalAssistantReportParameterMapping,
  ReportProjectionRule,
  TdFilterField,
} from './types';

const buildParameterSummary = (
  entry: GlobalAssistantReportCatalogEntry
): string =>
  entry.parameters.map((parameter) => parameter.displayName).join(', ');

const getEnabledCatalogEntries =
  (): readonly GlobalAssistantReportCatalogEntry[] =>
    GLOBAL_ASSISTANT_REPORT_CATALOG.filter((entry) => entry.enabled);

export const getEnabledReportTypes = (): ReportTypeEnum[] =>
  getEnabledCatalogEntries().map((entry) => entry.reportType);

const getCatalogEntry = (
  reportType: ReportTypeEnum
): GlobalAssistantReportCatalogEntry | undefined =>
  GLOBAL_ASSISTANT_REPORT_CATALOG.find(
    (entry) => entry.reportType === reportType && entry.enabled
  );

export const getGlobalAssistantReportCatalogEntry = (
  reportType: ReportTypeEnum
): GlobalAssistantReportOfferCatalogEntry | undefined => {
  const entry = getCatalogEntry(reportType);
  if (!entry) {
    return undefined;
  }
  return {
    reportType: entry.reportType,
    reportName: entry.reportName,
    description: entry.description,
    sampleLink: entry.sampleLink,
    parameterSummary: buildParameterSummary(entry),
  };
};

export const GLOBAL_AI_ASSISTANT_V1_REPORT_CATALOG: readonly GlobalAssistantReportOfferCatalogEntry[] =
  getEnabledCatalogEntries().map((entry) => ({
    reportType: entry.reportType,
    reportName: entry.reportName,
    description: entry.description,
    sampleLink: entry.sampleLink,
    parameterSummary: buildParameterSummary(entry),
  }));

export const getReportParameterCatalog = (
  reportType: ReportTypeEnum
): GlobalAssistantReportParameterCatalogEntry | undefined => {
  const entry = getCatalogEntry(reportType);
  if (!entry) {
    return undefined;
  }
  return {
    reportType: entry.reportType,
    parameters: entry.parameters,
  };
};

export const getReportParameterDescriptor = (
  reportType: ReportTypeEnum,
  parameterId: string
): GlobalAssistantReportParameterDescriptor | undefined =>
  getReportParameterCatalog(reportType)?.parameters.find(
    (parameter) => parameter.parameterId === parameterId
  );

export const formatReportParametersForPrompt = (
  reportType: ReportTypeEnum
): string => {
  const catalog = getReportParameterCatalog(reportType);
  if (!catalog) {
    return '';
  }
  return catalog.parameters
    .map((parameter) => {
      const multiLabel = parameter.supportsMultiple ? ', multiValue: true' : '';
      const listLabel = parameter.listFilterType
        ? `, listFilter: ${parameter.listFilterType}`
        : '';
      return `- parameterId: "${parameter.parameterId}", displayName: "${parameter.displayName}", resolver: ${parameter.resolverKind}, categories: [${parameter.expectedSelectionCategories.join(', ')}]${listLabel}${multiLabel}`;
    })
    .join('\n');
};

export const buildReportSelectorCatalogLines = (): string =>
  getEnabledCatalogEntries()
    .map(
      (entry) =>
        `- ${entry.reportType}: ${entry.reportName} — ${entry.description}. Parameters: ${buildParameterSummary(entry)}`
    )
    .join('\n');

const formatCapabilitySectionNames = (
  entry: GlobalAssistantReportCatalogEntry
): string =>
  entry.capabilityModel.sections.map((section) => section.name).join(', ');

const formatCapabilityReportBlock = (
  entry: GlobalAssistantReportCatalogEntry
): string => {
  const { capabilityModel } = entry;
  const outcomes = capabilityModel.outcomes
    .map((outcome) => `    - ${outcome}`)
    .join('\n');
  const bestFor = capabilityModel.bestForExamples
    .map((example) => `    - ${example}`)
    .join('\n');
  const notFor = capabilityModel.notFor
    .map((boundary) => `    - ${boundary}`)
    .join('\n');
  const selectionCues = capabilityModel.selectionCues
    ? capabilityModel.selectionCues.map((cue) => `    - ${cue}`).join('\n')
    : '';
  const selectionCuesBlock = selectionCues
    ? `\n  Selection cues:\n${selectionCues}`
    : '';

  return `- ${entry.reportType} (${entry.reportName}):
  Outcomes:
${outcomes}
  Sections: ${formatCapabilitySectionNames(entry)}
  Best for:
${bestFor}
  Not for:
${notFor}${selectionCuesBlock}`;
};

const buildReportSelectorCapabilityLines = (): string =>
  getEnabledCatalogEntries().map(formatCapabilityReportBlock).join('\n\n');

export const buildReportSelectorSystemPrompt = (): string => {
  const enabledTypes = getEnabledReportTypes();
  const reportTypeUnion = enabledTypes.map((type) => `"${type}"`).join(' | ');

  return `You select the best legacy Revelio report(s) for a user request.
Return JSON only:
{
  "reports": [{ "reportType": ${reportTypeUnion}, "confidence": 0-100 }],
  "reason": "short explanation or null"
}

Rules:
- Return up to 3 report types that clearly fit. Array order does not matter; reports are ranked by confidence.
- Use an empty reports array when no report capability model clearly fits.
- confidence is the single measure of fit for each report (0 = no match, 100 = perfect); set it to reflect how strongly each report matches.
- Only include reports you are confident fit; do not pad the list to reach 3.
- Reason from report outcomes, section names, best-for examples, and not-for boundaries below—not from keyword shortcuts alone.
- When the conversation includes an assistant clarifying question followed by the user's latest reply, judge report relevance from the combined original request plus the answer—not from the reply text alone (e.g. "company" after a scope question about a dashboard request).
- When the user's latest message is clearly a fresh independent analytics request rather than an answer to the clarifying question, judge report relevance for that new request only.
- Labor market conditions, hiring difficulty, and market tightness questions fit MarketTightness (Market Conditions) even when the user could also view the topic as a dashboard chart.
- Company prestige scores, employer reputation rankings, and prestige benchmarks for a named company fit Prestige (not Sentiment).

Report capability models:
${buildReportSelectorCapabilityLines()}

Available reports (metadata):
${buildReportSelectorCatalogLines()}`;
};

export const buildReportSelectorRetrySystemPrompt = (): string => {
  const enabledTypes = getEnabledReportTypes();
  const reportTypeUnion = enabledTypes.map((type) => `"${type}"`).join(' | ');

  return `You choose the best-fitting legacy Revelio report(s) for this user request.
Return JSON only:
{
  "reports": [{ "reportType": ${reportTypeUnion}, "confidence": 0-100 }],
  "reason": "short explanation or null"
}

Return up to 3 reports that clearly fit when multiple capability models match; otherwise an empty reports array. Array order does not matter—set each report's confidence (0-100) to reflect how strongly it fits, as confidence is the single ranking measure. Use the examples and boundaries in the capability models below—not memorized phrases.
- When the conversation includes an assistant clarifying question followed by the user's latest reply, judge report relevance from the combined original request plus the answer—not from the reply text alone.
- When the user's latest message is clearly a fresh independent analytics request rather than an answer to the clarifying question, judge report relevance for that new request only.

Report capability models:
${buildReportSelectorCapabilityLines()}`;
};

export const GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS: readonly {
  reportType: string;
  parameters: readonly GlobalAssistantReportParameterMapping[];
}[] = getEnabledCatalogEntries().map((entry) => ({
  reportType: entry.reportType,
  parameters: entry.parameters.map((parameter) => ({
    parameterId: parameter.parameterId,
    displayName: parameter.displayName,
    mwColumnLabels: parameter.mwColumnLabels,
    promotedParameterIds: parameter.promotedParameterIds,
  })),
}));

export const getReportParameterMapping = (
  reportType: string,
  parameterId: string
): GlobalAssistantReportParameterMapping | undefined =>
  GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS.find(
    (entry) => entry.reportType === reportType
  )?.parameters.find((parameter) => parameter.parameterId === parameterId);

export const getProjectionRules = (reportType: ReportTypeEnum) =>
  getCatalogEntry(reportType)?.projectionRules ?? [];
